import { createFileRoute, redirect } from '@tanstack/react-router';

import { BASE_FLOW_SUBSCRIPTION_ID } from '@brightlocal/shared';

import { ensureAuthMe } from 'shared/hooks';
import { routes } from 'shared/routes';

import { SignUp, isSignUpSubscriptionId } from 'features/auth/SignUp';
import { useGetStartedStore } from 'features/get-started/hooks';

export const Route = createFileRoute('/auth/sign-up/$id')({
  component: SignUpRoute,
  beforeLoad: async ({ context, params, search }) => {
    if (!isSignUpSubscriptionId(params.id)) {
      throw redirect({
        to: '/auth/sign-up/$id',
        params: { id: BASE_FLOW_SUBSCRIPTION_ID },
        search
      });
    }

    // isAuthenticated() checks the in-memory access token — it returns false after a page
    // reload even with a valid httpOnly cookie. Always call ensureAuthMe so cookie-based
    // refresh kicks in for returning users; the TanStack Query cache prevents extra requests
    // when the access token is already in memory.
    let authData;

    try {
      authData = await ensureAuthMe(context.queryClient);
    } catch {
      // Not authenticated — reset the store to ensure a fresh start of the data. The
      // onboarding branch is determined post-login from `subscription.packageId` returned
      // by `/auth/me`.
      useGetStartedStore.getState().reset();
      return;
    }

    // Users with existing locations are not mid-onboarding — send them to the locations
    // list (which applies its own feature-flag guard). Users with zero locations continue
    // to the onboarding flow.
    const hasLocations = authData.subscription.activeLocations > 0;

    throw redirect({
      to: hasLocations ? routes.locations() : routes.updateBusinessDetails(),
      search,
      replace: true
    });
  }
});

function SignUpRoute() {
  const { id } = Route.useParams();

  if (!isSignUpSubscriptionId(id)) {
    return null;
  }

  return <SignUp subscriptionId={id} />;
}
